import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { RingBuffer } from 'ring-buffer-ts';
// ngx-webcam no longer used for camera access
import {
  Subject,
  Observable,
  Subscription,
  timer,
  BehaviorSubject,
} from 'rxjs';
import { Platform } from '@ionic/angular';
import { DriverTemplate } from '../driver-template';
import { DriverControl } from '../nav-driver.service';
import { Driver } from 'src/app/nav-processing/mappings/mappings.model';
import { VoiceService } from 'src/app/voice/voice.service';
import { ProfileService } from 'src/app/profile/profile.service';
import { WebcamService, VideoFrameData } from '../webcam.service';

// export interface VisionState {
//   fps: number;
//   faceDataCallbacks: Function[];
// }
interface Category {
  index: number;
  score: number;
  display_name: string;
  category_name: string;
}

interface Classifications {
  categories: Category[];
}
interface NormalizedLandmark {
  x: number;
  y: number;
  z: number;
}
interface Matrix {
  rows: number;
  cols: number;
  data: number[];
}
interface FaceLandmarkerResult {
  faceLandmarks: NormalizedLandmark[][];
  faceBlendshapes: Classifications[];
  facialTransformationMatrixes: Matrix[];
}
export interface VisionDelay {
  start: number;
  frame?: number;
  bitmap?: number;
  handsData?: number;
  faceData?: number;
  controlAction?: number;
}

export interface VideoFrame {
  captureTime: number;
  data: ImageBitmap;
}

interface EyeTrackerPosition {
  scaleRef : Array<number>;
  rightRotation: number;
  leftRotation: number;
  upRotation: number;
  downRotation: number;
  leftOffset: number;
  rightOffset: number;
  downOffset: number;
  upOffset: number;
}


type VisionValidationResult = {
  issue:
    | 'ok'
    | 'visionServiceLoading'
    | 'issue image quality'
    | 'issue movement'
    | 'issue head direction'
    | 'issue too distant'
    | 'issue too close'
    | 'issue framerate'
    | 'no face';
  score?: number;
  distance?: number;
  direction?: number;
  positionStability?: number;
  meshStability?: number;
  framerate?: number;
};

@Injectable({
  providedIn: 'root',
})
export class VisionService extends DriverTemplate {
  //state: VisionState;

  meshRastreador: any;
  lastMeshReceived = Date.now();
  peyeMode = false;
  peyeQuality: any;
  peyeModeBad = false;
  voiceControl = '';
  //document: Document;
  videoElement: any; //: HTMLVideoElement;
  canvasElement: any; //: HTMLCanvasElement;
  canvasCtx: CanvasRenderingContext2D;

  public deviceId: string;
  public videoOptions: MediaTrackConstraints = {} as MediaTrackConstraints; // kept for compatibility if referenced elsewhere

  availableDevices: MediaDeviceInfo[] = {} as MediaDeviceInfo[];

  public errors: any[] = [];
  public showWebcam = true;

  visionServiceStatusBS: Subject<string> = new Subject<string>();

  vibrationBuffer: RingBuffer<number> | undefined;
  vibrationAverageBuffer: RingBuffer<number> | undefined;
  movementBuffer: RingBuffer<number> | undefined;

  serviceReady = false;
  appInForeground = true;

  mappingsPaused = false;

  lastIssue: VisionValidationResult = {
    issue: 'ok',
    score: 10,
    distance: 10,
    direction: 10,
    positionStability: 10,
    meshStability: 10,
    framerate: 10,
  };

  // Worker for running mediapipe inference outside the main interface thread
  faceWorker?: Worker;
  handsWorker?: Worker;
  private workersInitialized = false;

  nativeResult = undefined;

  handsWorkerIdle = true;
  faceWorkerIdle = true;

  webWorkerAvailable = false;

  debugVerbose = false;
  mirroredCamera = 'never';

  // handsDataCallback = (data: any) => {};

  handsDataObs = new Observable();
  faceDataObs = new Observable();

  leftHandIndex = 0;
  rightHandIndex = 1;

  // delay: VisionDelay = {} as VisionDelay;
  // delay2: VisionDelay = {} as VisionDelay;

 

  visionStatusValues = {
    initializing: 'grey-dot',
    searching: 'blue-dot',
    badCapture: 'red-dot',
    regularCapture: 'orange-dot',
    goodCapture: 'green-dot',
    pausedCapture: 'pause-dot',
  };

  overlayVoiceEnabled = false; // Disable overlay voice by default

  visionStatus: string = this.visionStatusValues.initializing;
  visionStatusTip = 'Inicializando';

  videoInitialized: boolean;

  // latest snapshot (legacy)
  public captureImage: any = null;

  //
  //private captureResultCallback: (WebcamImage) => void;

  // webcam snapshot trigger
  private captureTrigger: Subject<void> = new Subject<void>(); // legacy

  private cameraWaitInterval: any;
  private monitorInterval: any;

  private lastFrameTimestamp = 0;

  private frLastTimestamp = 0;
  private fr = 30; // Taxa de atualização em tempo real
  private averageFr = 30; // Taxa de atualização média

  private getFramesForQuality = 0;
  private getFramesForQualityRastreador = 0;

  private framesSub?: Subscription;

  problemPosition = 0;
  private hasIssue = false; //criei globalmente para acessar o valor em outro componente
  enablePositionScreen$ = new BehaviorSubject<boolean>(false);

  // switch to next / previous / specific webcam; true/false: forward/backwards, string: deviceId
  private nextWebcam: Subject<boolean | string> = new Subject<boolean | string>(); // legacy no-op

  constructor(
    private platform: Platform,
    private voiceService: VoiceService,
    private profile: ProfileService,
    private webcam: WebcamService
  ) {
    super();

    // this.state = {
    //   fps: 0,
    //   faceDataCallbacks: [],
    // };

    // Workers are not created in the constructor anymore to avoid loading models
    // until the Vision driver is actually enabled. They will be created lazily
    // inside init() via createWorkers().
  }

  get nextWebcamObservable(): Observable<boolean | string> {
    return this.nextWebcam.asObservable();
  }

  public get triggerObservable(): Observable<void> {
    return this.captureTrigger.asObservable();
  }

  async start(options?: DriverControl[]) {
    console.log('visionService enabled', this.myStatus.enabled);

    const drivers = options as DriverControl[];

    console.log('drivers', drivers);

    this.peyeMode =
      drivers
        ?.find((driver) => driver.driver === Driver.peyeTracker)
        ?.service?.isEnabled() || false;

    console.log('[VisionService] peyeMode ', this.peyeMode);

    if (this.myStatus.enabled) {
      return Promise.resolve(false);
    } else {
      this.myStatus.enabled = true;
      console.log('visionService start');
      return this.init();
    }
  }

  async stop() {
    console.log('visionService stop');
    if (this.myStatus.enabled) {
      this.serviceReady = false;
      // Atualização de fr media
      clearInterval(this.monitorInterval);
      try { this.framesSub?.unsubscribe(); } catch {}
      // Make sure workers and loaded models are terminated/unloaded when stopping
      try { await this.terminateWorkers(); } catch (e) { console.warn('Error terminating workers', e); }
      this.myStatus.enabled = false;
      this.myStatus.running = false;
      this.myStatus.capturing = false;
      this.visionServiceStatusBS.next('visionServiceStopped');
      return Promise.resolve(true);
    } else {
      return Promise.resolve(false);
    }
  }

  receiveMeshRastreador(data: any, eyeMode: EyeTrackerPosition) {
    //função que recebe face mesh do rastreador ocular
    //console.log("vision service receiveMeshRastreador", data);
    this.lastMeshReceived = Date.now();
    this.meshRastreador = data;

    this.processFramesForQualityRastreador(this.meshRastreador, eyeMode);
    //logica para encaminhar dados pro mappings pra identificar gestos
    const resultado = this.convertToFaceLandmarkerResult(this.meshRastreador);
    //console.log("receiveMesh result ", resultado);

    let dataMappings = {
      captureTime: this.lastMeshReceived,
      result: resultado,
    };
    this.captureResultCallback(dataMappings);
  }

  convertToFaceLandmarkerResult(rawData: any): FaceLandmarkerResult {
    if (!Array.isArray(rawData) || rawData.length < 2) {
      throw new Error('Formato de entrada inválido!');
    }

    const faceLandmarks = this.parseLandmarks(rawData[0]);
    const faceBlendshapes = this.parseCategories(rawData[1]);

    // Como não temos matriz, deixamos um array vazio
    const facialTransformationMatrixes: Matrix[] = [];

    return {
      faceLandmarks,
      faceBlendshapes,
      facialTransformationMatrixes, // Array vazio
    };
  }

  parseLandmarks(landmarkString: string): NormalizedLandmark[][] {
    // Expressão regular para encontrar valores de x, y, z
    const regex = /x:\s*([\d\.-]+)\s*y:\s*([\d\.-]+)\s*z:\s*([\d\.-]+)/g;
    let match;
    let landmarks: NormalizedLandmark[] = [];

    while ((match = regex.exec(landmarkString)) !== null) {
      landmarks.push({
        x: parseFloat(match[1]),
        y: parseFloat(match[2]),
        z: parseFloat(match[3]),
      });
    }

    landmarks = this.fixLandmarks(landmarks);

    return [landmarks]; // Retornamos um array de arrays para corresponder ao formato esperado
  }

  fixLandmarks(
    landmarks: { x: number; y: number; z: number }[]
  ): { x: number; y: number; z: number }[] {
    const expectedIndices = Array.from({ length: 478 }, (_, i) => i); // Índices esperados de 0 a 477
    const actualIndices = landmarks.map((_, index) => index); // Índices reais detectados

    // Encontrar o índice ausente
    const missingIndex = expectedIndices.find(
      (index) => !actualIndices.includes(index)
    );

    if (missingIndex !== undefined) {
      console.warn(
        `⚠️ Landmark ausente no índice ${missingIndex}, adicionando ponto nulo.`
      );

      // Adicionar o ponto nulo na posição exata
      landmarks.splice(missingIndex, 0, { x: 0, y: 0, z: 0 });
    }

    return landmarks;
  }

  parseCategories(categoryString: string): Classifications[] {
    try {
      // 1. Transformar a string no formato JSON válido
      let formattedString = categoryString
        .replace(/Category\(/g, '{') // Substituir "Category(" por "{"
        .replace(/\)/g, '}') // Substituir ")" por "}"
        .replace(/=/g, ':') // Substituir "=" por ":"
        .replace(/(\w+):/g, '"$1":') // Adicionar aspas às chaves JSON
        .replace(/'/g, '"'); // Substituir aspas simples por aspas duplas

      // 2. Garantir que a string esteja dentro de um array JSON válido
      if (!formattedString.startsWith('[')) {
        formattedString = `[${formattedString}]`;
      }

      // 3. Fazer o parsing do JSON
      const categoriesArray = JSON.parse(formattedString);

      // 4. Mapear para a estrutura esperada
      return [
        {
          categories: categoriesArray.map((cat: any) => ({
            index: cat.index,
            score: cat.score,
            display_name: cat.display_name || '',
            category_name: cat.category_name || '',
          })),
        },
      ];
    } catch (error) {
      console.error('Erro ao parsear categorias:', error);
      return [];
    }
  }

  async init() {
    //console.log('visionServiceLoading');
    // Atualização de fr media
    clearInterval(this.monitorInterval);
    this.monitorInterval = setInterval(() => this.visionStatusMonitor(), 500);

    // this.faceWorker.onmessage = ({ data }) => this.faceWorkerCallback(data);
    // this.faceWorker.postMessage({ type: 'init', production: environment.production });

    if (this.serviceReady) {
      return Promise.resolve(true);
    }
    this.lastFrameTimestamp = Date.now();

    this.visionServiceStatusBS.next('visionServiceLoading'); // Tell everyone we are need some time to get ready!!!

    // Inicializa estatisticas de qualidade
    this.vibrationBuffer = new RingBuffer<number>(5);
    this.movementBuffer = new RingBuffer<number>(30);
    this.vibrationAverageBuffer = new RingBuffer<number>(30);

    // Create workers (and load models) only when vision driver is initialized.
    await this.createWorkers();

    // Aguarda para inicializar mediapipe
    this.registerVideoSource();

    return Promise.resolve(true);
  }

  registerVideoSource() {
    // Start the centralized webcam service and subscribe to frames
    this.visionStatus = this.visionStatusValues.initializing;
    this.visionStatusTip = '';
    this.serviceReady = false;
    this.visionServiceStatusBS.next('visionServiceLoading');

    // Start or reuse webcam stream
    this.webcam
      .start({ preferFrontCamera: true, withAudio: false, frameRate: 30 })
      .then(() => {
        // Enable frame broadcasting if not already
        this.webcam.setFrameBroadcastEnabled(true);
        // Avoid duplicate subscriptions
        this.framesSub?.unsubscribe();
        this.framesSub = this.webcam.frames$.subscribe((frame: VideoFrameData) => {
          this.onVideoFrame(frame);
        });
      })
      .catch((err) => {
        console.error('[VisionService] webcam start failed', err);
        this.visionStatus = this.visionStatusValues.badCapture;
        this.visionStatusTip = 'Erro na câmera';
      });
  }

  //cameraHelper: Camera;

  // Handle incoming frames from WebcamService
  private onVideoFrame(frame: VideoFrameData) {
    //console.log('onVideoFrame', frame);
    this.lastFrameTimestamp = Date.now();
    if (!this.myStatus.enabled || !this.webWorkerAvailable || !this.faceWorker) {
      // not enabled, or workers not created yet: discard frame
      frame.bitmap.close();
      return;
    }
    if (!this.faceWorkerIdle) { frame.bitmap.close(); return; }

    const payload: VideoFrame = { captureTime: frame.captureTime, data: frame.bitmap };
    this.faceWorkerIdle = false;
    // Transfer ownership of the bitmap to the worker
    try {
      this.faceWorker.postMessage(payload, [frame.bitmap]);
    } catch (err) {
      // If postMessage fails (e.g. due to worker being terminated concurrently)
      console.warn('Failed to post frame to faceWorker', err);
      try { frame.bitmap.close(); } catch {}
      this.faceWorkerIdle = true;
      return;
    }

    if (!this.serviceReady) {
      this.serviceReady = true;
      this.visionServiceStatusBS.next('visionServiceReady');
    }
  }

  faceWorkerCallback(data: {
    captureTime: number;
    result: FaceLandmarkerResult;
  }) {
    this.myStatus.running = true;
    this.faceWorkerIdle = true;
    //console.log('faceWorkerCallback', performance.now() - data.captureTime);
    //nativePlugin.nativeResult({ value: 'nativeResult' }).then(value => {    console.log('Response from nativeResult:', value.value);
    //}); //teste chamando uma função do native, no futuro vai receber o processamento do media pipe

    //this.state.faceDataCallbacks.forEach((fn) => fn(data));
    let timeRastreador = Date.now() - this.lastMeshReceived;
    if (timeRastreador > 1000) {
      if (this.peyeMode === false) {
        this.captureResultCallback(data);
      }

      this.processFramesForQuality(data);
    }
  }

  setVideoElement(video: HTMLVideoElement) {
    this.videoElement = video;
  }

  getVideoElement() {
    return this.videoElement;
  }

  setCanvasElement(canvas: HTMLCanvasElement) {
    this.canvasElement = canvas;
    this.canvasCtx = this.canvasElement.getContext('2d');
  }

  public toggleWebcam(): void {
    this.showWebcam = !this.showWebcam;
  }

  public handleInitError(error: any): void {
    this.errors.push(error);
    if (
      error.mediaStreamError &&
      error.mediaStreamError.name === 'NotAllowedError'
    ) {
      console.warn('Camera access was not allowed by user!');
      this.myStatus.running = false;
    }
  }

  // public showNextWebcam(directionOrDeviceId: boolean | string): void {
  //   // true => move forward through devices
  //   // false => move backwards through devices
  //   // string => move to device with given deviceId

  //   this.nextWebcam.next(directionOrDeviceId);
  // }

  // public handleImageCapture(webcamImage: any): void {
  //   // console.log('received webcam image', webcamImage);
  //   // this.captureImage = webcamImage;
  //   // this.captureResultCallback(webcamImage);
  // }

  // cameraWasSwitched(deviceId: any): void {
  //   //console.log('active device: ' + deviceId);
  //   this.deviceId = deviceId;
  //   localStorage.setItem('webcamId', this.deviceId);
  //   this.registerVideoSource();
  // }

  processFramesForQualityRastreador(data: any, eyePosition: EyeTrackerPosition) {
    let timeRastreador = Date.now() - this.lastMeshReceived;
    if (timeRastreador < 1000) {
      if (this.getFramesForQualityRastreador) {
        if (this.getFramesForQualityRastreador === 1) {
          console.log(
            'processFramesForQualityRastreador',
            this.getFramesForQualityRastreador
          );

          const glasses =
            this.profile.profile.navSettingsBundle.bundleOptionsState
              ?.rastreadorGlasses;

          const result = data;
          //console.log("vision processFramesForQuality", result);
          this.hasIssue = false;

          let visionStatusTip = '';

          const landmarks_original = result[0]; // Garantindo que é string

          const pattern = /x: ([\d.-]+)\s+y: ([\d.-]+)\s+z: ([\d.-]+)/g;
          const matches = Array.from(landmarks_original.matchAll(pattern));

          // Índices desejados
          const selectedIndices = new Set([4, 36, 50, 164, 168, 266, 280]);

          // Filtrar apenas os índices escolhidos
          const landmarks = matches
            .map((match, index) => {
              if (!selectedIndices.has(index)) return null; // Pula se não estiver na lista
              return {
                x: parseFloat(match[1]),
                y: parseFloat(match[2]),
                z: parseFloat(match[3]),
              };
            })
            .filter((item) => item !== null); // Remove valores nulos


          //check the size of landmarks to see if it is capturing
          if (landmarks.length > 0) {
            this.myStatus.capturing = true;
            const a = landmarks[2];
            const b = landmarks[6];

            const scaleRef = Math.sqrt(
              Math.pow(b.x - a.x, 2) +
                Math.pow(b.y - a.y, 2) +
                Math.pow(b.z - a.z, 2)
            ); //quanto mais próximo da tela maior

            console.log("Position scaleref", scaleRef);


            let rightRotation = landmarks[0].x - landmarks[1].x < eyePosition.rightRotation;
            let leftRotation = landmarks[5].x - landmarks[0].x < eyePosition.leftRotation;
            let upRotation = landmarks[0].y - landmarks[4].y < eyePosition.upRotation;
            let upRotationGlasses = landmarks[0].y - landmarks[4].y < 0.16;
            let downRotation = landmarks[3].y - landmarks[0].y < eyePosition.downRotation;

            const leftOffset = landmarks[0].x > eyePosition.leftOffset;
            const rightOffset = landmarks[0].x < eyePosition.rightOffset;
            const downOffset = landmarks[0].y > eyePosition.downOffset;
            const upOffset = landmarks[0].y < eyePosition.upOffset;

           

            const voiceEnable =
              this.profile.navSettingsBundle?.bundleOptionsState
                ?.speakPositioningTips;
            //console.log('voiceEnable', voiceEnable);

            if (scaleRef < eyePosition.scaleRef[0]) {
              this.hasIssue = true;

              visionStatusTip = 'Aproxime';
              if (
                this.voiceControl != 'Aproxime' &&
                (voiceEnable === true || this.overlayVoiceEnabled === true)
              ) {
                this.voiceService.speak(
                  'Aproxime',
                  'peye-position-orientation'
                );
                this.voiceControl = 'Aproxime';
              }
            } else if (scaleRef > eyePosition.scaleRef[1]) {
              this.hasIssue = true;

              visionStatusTip = 'Afaste';
              if (
                this.voiceControl != 'Afaste' &&
                (voiceEnable === true || this.overlayVoiceEnabled === true)
              ) {
                this.voiceService.speak('Afaste', 'peye-position-orientation');
                this.voiceControl = 'Afaste';
              }
            } else if (rightRotation) {
              this.hasIssue = true;

              visionStatusTip = 'Vire <-';
              if (
                this.voiceControl != 'Vire para a esquerda' &&
                (voiceEnable === true || this.overlayVoiceEnabled === true)
              ) {
                this.voiceService.speak(
                  'Vire para a esquerda',
                  'peye-position-orientation'
                );
                this.voiceControl = 'Vire para a esquerda';
              }
            } else if (leftRotation) {
              this.hasIssue = true;

              visionStatusTip = 'Vire ->';
              if (
                this.voiceControl != 'Vire para a direita' &&
                voiceEnable === true
              ) {
                this.voiceService.speak(
                  'Vire para a direita',
                  'peye-position-orientation'
                );
                this.voiceControl = 'Vire para a direita';
              }
            } else if (rightOffset) {
              this.hasIssue = true;

              visionStatusTip = 'Desloque <-';
              if (
                this.voiceControl != 'Desloque para a esquerda' &&
                voiceEnable === true
              ) {
                this.voiceService.speak(
                  'Desloque para a esquerda',
                  'peye-position-orientation'
                );
                this.voiceControl = 'Desloque para a esquerda';
              }
            } else if (leftOffset) {
              this.hasIssue = true;

              visionStatusTip = 'Desloque ->';
              if (
                this.voiceControl != 'Desloque para a direita' &&
                voiceEnable === true
              ) {
                this.voiceService.speak(
                  'Desloque para a direita',
                  'peye-position-orientation'
                );
                this.voiceControl = 'Desloque para a direita';
              }
            } else if (upRotation) {
              this.hasIssue = true;

              visionStatusTip = 'Vire \\/';
              if (
                this.voiceControl != 'Vire para baixo' &&
                voiceEnable === true
              ) {
                this.voiceService.speak(
                  'Vire para baixo',
                  'peye-position-orientation'
                );
                this.voiceControl = 'Vire para baixo';
              }
            } else if (downRotation) {
              this.hasIssue = true;

              visionStatusTip = 'Vire /\\';
              if (
                this.voiceControl != 'Vire para cima' &&
                voiceEnable === true
              ) {
                this.voiceService.speak(
                  'Vire para cima',
                  'peye-position-orientation'
                );
                this.voiceControl = 'Vire para cima';
              }
            } else if (upOffset) {
              this.hasIssue = true;

              visionStatusTip = 'Desloque \\/';
              if (
                this.voiceControl != 'Desloque para baixo' &&
                voiceEnable === true
              ) {
                this.voiceService.speak(
                  'Desloque para baixo',
                  'peye-position-orientation'
                );
                this.voiceControl = 'Desloque para baixo';
              }
            } else if (downOffset) {
              this.hasIssue = true;

              visionStatusTip = 'Desloque /\\';
              if (
                this.voiceControl != 'Desloque para cima' &&
                voiceEnable === true
              ) {
                this.voiceService.speak(
                  'Desloque para cima',
                  'peye-position-orientation'
                );
                this.voiceControl = 'Desloque para cima';
              }
            }

            if (this.hasIssue) {
              this.visionStatus = this.visionStatusValues.regularCapture;
              this.visionStatusTip = visionStatusTip;
              this.problemPosition = 1;
            } else if (this.mappingsPaused) {
              this.visionStatus = this.visionStatusValues.pausedCapture;
              this.visionStatusTip = 'Pausado';
            } else {
              this.visionStatus = this.visionStatusValues.goodCapture;
              this.visionStatusTip = '';
              this.problemPosition = 0;
            }
          } else {
            this.hasIssue = true;
            this.visionStatus = this.visionStatusValues.searching;
            this.visionStatusTip = 'Procurando';
            this.myStatus.capturing = false;
          }
        }
        this.getFramesForQualityRastreador--;
      }
    }
  }

  processFramesForQuality(data: {
    captureTime: number;
    result: FaceLandmarkerResult;
  }) {
    let timeRastreador = Date.now() - this.lastMeshReceived;
    if (timeRastreador >= 1000) {
      if (this.getFramesForQuality) {
        if (this.getFramesForQuality === 1) {
          // console.log(
          //   'processFramesForQualityNormal',
          //   this.getFramesForQuality
          // );

          const result = data.result;
          let hasIssue = false;

          let visionStatusTip = '';
          // Registra Framerate
          const diff = Date.now() - this.frLastTimestamp;
          this.fr = Math.floor(1000 / diff);
          this.averageFr = this.averageFr * 0.9 + this.fr * 0.1;
          // Head orientation
          if (result.faceLandmarks.length > 0) {
            this.myStatus.capturing = true;
            const a = result.faceLandmarks[0][50];
            const b = result.faceLandmarks[0][280];
            const scaleRef = Math.sqrt(
              Math.pow(b.x - a.x, 2) +
                Math.pow(b.y - a.y, 2) +
                Math.pow(b.z - a.z, 2)
            );

            const leftOffset = result.faceLandmarks[0][4].x;
            const rightOffset = result.faceLandmarks[0][4].x;
            const downOffset = result.faceLandmarks[0][4].y;
            const upOffset = result.faceLandmarks[0][4].y;

            const left =
              result.faceLandmarks[0][4].x > result.faceLandmarks[0][266].x;
            const right =
              result.faceLandmarks[0][4].x < result.faceLandmarks[0][36].x;

            const leftPeye =
              result.faceLandmarks[0][4].x >
              result.faceLandmarks[0][266].x - 0.04;
            const rightPeye =
              result.faceLandmarks[0][4].x <
              result.faceLandmarks[0][36].x + 0.03;

            //197 + 0.05 outra opcao
            const upPeye =
              result.faceLandmarks[0][4].y - result.faceLandmarks[0][168].y <
              0.095;
            const downPeye =
              result.faceLandmarks[0][164].y - result.faceLandmarks[0][4].y <
              0.03;

            const voiceEnable =
              this.profile.navSettingsBundle?.bundleOptionsState
                ?.speakPositioningTips;
            //console.log('voiceEnable', voiceEnable);

            if (this.peyeMode === true) {
              //visão para orientar a posição da pessoa ao usar o peye
              if (scaleRef < 0.17) {
                this.peyeModeBad = false;
                hasIssue = true;

                visionStatusTip = 'Aproxime';
                if (
                  this.voiceControl != 'Aproxime' &&
                  (voiceEnable === true || this.overlayVoiceEnabled === true)
                ) {
                  this.voiceService.speak(
                    'Aproxime',
                    'peye-position-orientation'
                  );
                  this.voiceControl = 'Aproxime';
                }
              } else if (scaleRef > 0.2) {
                hasIssue = true;

                this.peyeModeBad = false;
                visionStatusTip = 'Afaste';
                if (
                  this.voiceControl != 'Afaste' &&
                  (voiceEnable === true || this.overlayVoiceEnabled === true)
                ) {
                  this.voiceService.speak(
                    'Afaste',
                    'peye-position-orientation'
                  );
                  this.voiceControl = 'Afaste';
                }
              } else if (rightPeye) {
                hasIssue = true;

                this.peyeModeBad = false;
                visionStatusTip = 'Vire <-';
                if (
                  this.voiceControl != 'Vire para a esquerda' &&
                  voiceEnable === true
                ) {
                  this.voiceService.speak(
                    'Vire para a esquerda',
                    'peye-position-orientation'
                  );
                  this.voiceControl = 'Vire para a esquerda';
                }
              } else if (leftPeye) {
                hasIssue = true;

                this.peyeModeBad = false;
                visionStatusTip = 'Vire ->';
                if (
                  this.voiceControl != 'Vire para a direita' &&
                  voiceEnable === true
                ) {
                  this.voiceService.speak(
                    'Vire para a direita',
                    'peye-position-orientation'
                  );
                  this.voiceControl = 'Vire para a direita';
                }
              } else if (rightOffset < 0.4) {
                hasIssue = true;

                this.peyeModeBad = false;
                visionStatusTip = 'Desloque <-';
                if (
                  this.voiceControl != 'Desloque para a esquerda' &&
                  voiceEnable === true
                ) {
                  this.voiceService.speak(
                    'Desloque para a esquerda',
                    'peye-position-orientation'
                  );
                  this.voiceControl = 'Desloque para a esquerda';
                }
              } else if (leftOffset > 0.58) {
                hasIssue = true;

                this.peyeModeBad = false;
                visionStatusTip = 'Desloque ->';
                if (
                  this.voiceControl != 'Desloque para a direita' &&
                  voiceEnable === true
                ) {
                  this.voiceService.speak(
                    'Desloque para a direita',
                    'peye-position-orientation'
                  );
                  this.voiceControl = 'Desloque para a direita';
                }
              } else if (upPeye) {
                hasIssue = true;

                this.peyeModeBad = false;
                visionStatusTip = 'Vire \\/';
                if (
                  this.voiceControl != 'Vire para baixo' &&
                  voiceEnable === true
                ) {
                  this.voiceService.speak(
                    'Vire para baixo',
                    'peye-position-orientation'
                  );
                  this.voiceControl = 'Vire para baixo';
                }
              } else if (downPeye) {
                hasIssue = true;

                this.peyeModeBad = false;
                visionStatusTip = 'Vire /\\';
                if (
                  this.voiceControl != 'Vire para cima' &&
                  voiceEnable === true
                ) {
                  this.voiceService.speak(
                    'Vire para cima',
                    'peye-position-orientation'
                  );
                  this.voiceControl = 'Vire para cima';
                }
              } else if (upOffset < 0.62) {
                hasIssue = true;

                this.peyeModeBad = false;
                visionStatusTip = 'Desloque \\/';
                if (
                  this.voiceControl != 'Desloque para baixo' &&
                  voiceEnable === true
                ) {
                  this.voiceService.speak(
                    'Desloque para baixo',
                    'peye-position-orientation'
                  );
                  this.voiceControl = 'Desloque para baixo';
                }
              } else if (downOffset > 0.8) {
                hasIssue = true;

                this.peyeModeBad = false;
                visionStatusTip = 'Desloque /\\';
                if (
                  this.voiceControl != 'Desloque para cima' &&
                  voiceEnable === true
                ) {
                  this.voiceService.speak(
                    'Desloque para cima',
                    'peye-position-orientation'
                  );
                  this.voiceControl = 'Desloque para cima';
                }
              } else if (this.peyeQuality === 'Bom') {
                hasIssue = false;

                this.peyeModeBad = false;
              } else if (this.peyeQuality === 'Médio') {
                hasIssue = true;

                this.peyeModeBad = false;
                visionStatusTip = 'Rastreamento Médio';
              } else if (this.peyeQuality === 'Ruim') {
                hasIssue = true;

                this.peyeModeBad = true;
                visionStatusTip = 'Rastreamento Ruim';
              }
              // else {
              //   this.voiceControl = '';
              // }
            } else {
              //visão normalmente
              if (scaleRef < 0.08) {
                hasIssue = true;

                visionStatusTip = 'Aproxime';

                if (
                  this.voiceControl != 'Aproxime' &&
                  (voiceEnable === true || this.overlayVoiceEnabled === true)
                ) {
                  this.voiceService.speak('Aproxime', 'position-orientation');
                  this.voiceControl = 'Aproxime';
                }
              } else if (scaleRef > 0.3) {
                hasIssue = true;

                visionStatusTip = 'Afaste';

                if (
                  this.voiceControl != 'Afaste' &&
                  (voiceEnable === true || this.overlayVoiceEnabled === true)
                ) {
                  this.voiceService.speak('Afaste', 'position-orientation');
                  this.voiceControl = 'Afaste';
                }
              } else if (right) {
                hasIssue = true;

                visionStatusTip = 'Vire <-';

                if (
                  this.voiceControl != 'Vire para a esquerda' &&
                  (voiceEnable === true || this.overlayVoiceEnabled === true)
                ) {
                  this.voiceService.speak(
                    'Vire para a esquerda',
                    'position-orientation'
                  );
                  this.voiceControl = 'Vire para a esquerda';
                }
              } else if (left) {
                hasIssue = true;

                visionStatusTip = 'Vire ->';

                if (
                  this.voiceControl != 'Vire para a direita' &&
                  (voiceEnable === true || this.overlayVoiceEnabled === true)
                ) {
                  this.voiceService.speak(
                    'Vire para a direita',
                    'position-orientation'
                  );
                  this.voiceControl = 'Vire para a direita';
                }
              } else if (rightOffset < 0.3) {
                hasIssue = true;

                visionStatusTip = 'Desloque <-';

                if (
                  this.voiceControl != 'Desloque para a esquerda' &&
                  (voiceEnable === true || this.overlayVoiceEnabled === true)
                ) {
                  this.voiceService.speak(
                    'Desloque para a esquerda',
                    'position-orientation'
                  );
                  this.voiceControl = 'Desloque para a esquerda';
                }
              } else if (leftOffset > 0.7) {
                hasIssue = true;

                visionStatusTip = 'Desloque ->';

                if (
                  this.voiceControl != 'Desloque para a direita' &&
                  (voiceEnable === true || this.overlayVoiceEnabled === true)
                ) {
                  this.voiceService.speak(
                    'Desloque para a direita',
                    'position-orientation'
                  );
                  this.voiceControl = 'Desloque para a direita';
                }
              } else if (upOffset < 0.3) {
                hasIssue = true;

                visionStatusTip = 'Desloque \\/';

                if (
                  this.voiceControl != 'Desloque para baixo' &&
                  (voiceEnable === true || this.overlayVoiceEnabled === true)
                ) {
                  this.voiceService.speak(
                    'Desloque para baixo',
                    'position-orientation'
                  );
                  this.voiceControl = 'Desloque para baixo';
                }
              } else if (downOffset > 0.7) {
                hasIssue = true;

                visionStatusTip = 'Desloque /\\';

                if (
                  this.voiceControl != 'Desloque para cima' &&
                  (voiceEnable === true || this.overlayVoiceEnabled === true)
                ) {
                  this.voiceService.speak(
                    'Desloque para cima',
                    'position-orientation'
                  );
                  this.voiceControl = 'Desloque para cima';
                }
              }
              // else {
              //   // TODO: CONFIRMAR SE É AQUI QUE TEM QUE FALAR O "PROCURANDO"
              //   hasIssue = true;

              //   visionStatusTip = 'Procurando'

              //    if (this.voiceControl != 'Procurando' && (voiceEnable === true || this.overlayVoiceEnabled === true)) {
              //     this.voiceService.speak('Procurando', 'position-orientation');
              //     this.voiceControl = 'Procurando';
              //    }
              // }
            }

            if (hasIssue && this.peyeModeBad === false) {
              this.visionStatus = this.visionStatusValues.regularCapture;
              this.visionStatusTip = visionStatusTip;
            } else if (this.mappingsPaused) {
              this.visionStatus = this.visionStatusValues.pausedCapture;
              this.visionStatusTip = 'Pausado';
            } else if (hasIssue && this.peyeModeBad === true) {
              this.visionStatus = this.visionStatusValues.badCapture;
              this.visionStatusTip = visionStatusTip;
            } else {
              this.visionStatus = this.visionStatusValues.goodCapture;
              this.visionStatusTip = '';
            }
          } else {
            hasIssue = true;
            this.visionStatus = this.visionStatusValues.searching;
            this.visionStatusTip = 'Procurando';
            this.myStatus.capturing = false;
          }
        } else {
          this.frLastTimestamp = Date.now();
        }

        this.getFramesForQuality--;
      }
    }
  }

  visionStatusMonitor() {
    if (this.appInForeground && this.myStatus.enabled) {
      this.getFramesForQuality = 2;
      this.getFramesForQualityRastreador = 2;
      //console.log('visionStatusMonitor');

      const timeSInceLatsFRame = Date.now() - this.lastFrameTimestamp;

      if (environment.environmentName === 'prod') {
        if (timeSInceLatsFRame > 10000 && this.lastFrameTimestamp) {
          //document.location.reload();
          this.registerVideoSource();
          this.lastFrameTimestamp = Date.now();
        }
      }

      // Ausência de frames
      else if (timeSInceLatsFRame >= 4000 && timeSInceLatsFRame < 6000) {
        this.registerVideoSource();
      }

      // Framerate

      // Face too far

      //console.log('[visionStatusMonitor]', this.averageFr, timeSInceLatsFRame);
    } else {
      //console.log('[visionStatusMonitor] app in background');
    }
  }

  /**
   * Returns a promise containing and WebcamImage with the current camera image
   */
  // getNextFrame() {
  //   return new Promise<WebcamImage>((resolve, reject) => {
  //     this.captureResultCallback = (image: WebcamImage) => {
  //       console.log('captureResultCallback', image);
  //       resolve(image);
  //     };
  //     this.captureTrigger.next();
  //   });
  // }

  /**
   * Lazily create web workers and initialize the models in them. This is idempotent.
   */
  private async createWorkers(): Promise<void> {
    if (this.workersInitialized) return;

    if (typeof Worker === 'undefined') {
      this.webWorkerAvailable = false;
      console.log('Web Workers are not supported in this environment.');
      return;
    }

    this.webWorkerAvailable = true;

    // Create workers
    try {
      this.faceWorker = new Worker(new URL('./face.worker', import.meta.url));
      this.handsWorker = new Worker(new URL('./hands.worker', import.meta.url));

      // Wire face worker messages & init
      this.faceWorker.onmessage = ({ data }) => {
        if (data) {
          this.faceWorkerCallback(data);
        } else {
          this.faceWorkerIdle = true; // dropped frame
        }
      };

      // handsWorker currently not piped into anything here, but keep a safe handler
      this.handsWorker.onmessage = ({ data }) => {
        // forward or process hands data in future; ignore for now
      };

      // Send init message to workers to have them load models
      try {
        this.faceWorker.postMessage({ type: 'init', production: environment.production });
      } catch (e) {
        console.warn('Failed to initialize faceWorker', e);
      }
      try {
        this.handsWorker.postMessage({ type: 'init', production: environment.production });
      } catch (e) {
        // hands.worker doesn't necessarily expect an init message, but safe to try
      }

      this.workersInitialized = true;
      this.faceWorkerIdle = true;
      this.handsWorkerIdle = true;
    } catch (error) {
      console.error('Error creating vision workers', error);
      this.webWorkerAvailable = false;
      this.workersInitialized = false;
    }
  }

  /**
   * Terminates workers and frees loaded models. Safe to call multiple times.
   */
  private async terminateWorkers(): Promise<void> {
    this.workersInitialized = false;

    try {
      if (this.faceWorker) {
        try { this.faceWorker.terminate(); } catch (e) { console.warn('Face worker termination failed', e); }
        this.faceWorker = undefined;
      }
    } finally {
      this.faceWorkerIdle = true;
    }

    try {
      if (this.handsWorker) {
        try { this.handsWorker.terminate(); } catch (e) { console.warn('Hands worker termination failed', e); }
        this.handsWorker = undefined;
      }
    } finally {
      this.handsWorkerIdle = true;
    }

    this.webWorkerAvailable = false;
  }
}
